// Screen 13 — Recharge-request queue (multi-tenant).
//
// The inbox of per-account recharge (top-up) requests across all tenants. Each
// row shows the request + client (resolved from /admin/clients), the target
// account id, the requested amount, a BUDGET CHECK badge (within-balance vs
// exceeds-balance, resolved server-side via budget_ok), a Stepper of the
// recharge state machine, and an Advance control that fires the next transition
// — or Reject as the terminal off-ramp.
//
// REAL SHAPES (admin Go handlers — source of truth):
//   GET  /admin/recharge-requests?status=&client_id=&account_id=&limit=&offset=
//        -> { recharge_requests: AdminRechargeRequest[] }
//   POST /admin/recharge-requests/{id}/{request-to-agency|topped-up|done|reject}
// The recharge state machine: requested → requested_to_agency → topped_up →
// done. Rejected is a terminal off-ramp surfaced as a danger pill (not a stepper
// position). The backend row carries client_id / account_id / amount / status /
// budget_ok — there is NO client or account NAME, so we resolve the client name
// from /admin/clients and show the raw account_id.
//
// MONEY: the requested amount is an exact decimal STRING rendered via MoneyCell
// — never parsed to a float. The budget check is the backend's call; the UI only
// surfaces the boolean it returns (we never recompute a client's balance here).

import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "@/api/client";
import { errMessage } from "@/lib/errMessage";
import { AdminShell } from "@/components/nexus/AdminShell";
import {
  DataTable,
  MoneyCell,
  Stepper,
  ToneBadge,
  type Column,
  type Step,
} from "@/components/nexus";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { NativeSelect, Pager } from "./controls";

// The admin queue row, exactly as the backend serializes it.
interface RechargeRequest {
  id: string;
  client_id: string;
  account_id: string;
  amount: string;
  status: RechargeState;
  budget_ok: boolean;
  source?: string;
}

type RechargeState =
  | "requested"
  | "requested_to_agency"
  | "topped_up"
  | "done"
  | "rejected";

const STEPS: Step[] = [
  { key: "requested", label: "Requested" },
  { key: "requested_to_agency", label: "To agency" },
  { key: "topped_up", label: "Topped up" },
  { key: "done", label: "Done" },
];

// Map a state to its stepper index and the transition that advances it.
const FLOW: Record<
  RechargeState,
  { index: number; next?: { transition: string; label: string } }
> = {
  requested: {
    index: 0,
    next: { transition: "request-to-agency", label: "Send to agency" },
  },
  requested_to_agency: {
    index: 1,
    next: { transition: "topped-up", label: "Mark topped up" },
  },
  topped_up: { index: 2, next: { transition: "done", label: "Complete" } },
  done: { index: 3 },
  rejected: { index: 0 },
};

function flowOf(state: RechargeState): { index: number; next?: { transition: string; label: string } } {
  return FLOW[state] ?? { index: 0 };
}

// Terminal states the default "Open" view hides (toggle to show).
const TERMINAL_STATES: readonly string[] = ["done", "rejected"];

// Every backend status, for the explicit server-side ?status filter.
const ALL_STATUSES: readonly RechargeState[] = [
  "requested",
  "requested_to_agency",
  "topped_up",
  "done",
  "rejected",
];

const PAGE_SIZE = 50;

interface ClientRef {
  id: string;
  name: string;
}

export function RechargeRequestsPage() {
  const [requests, setRequests] = useState<RechargeRequest[]>([]);
  const [clients, setClients] = useState<ClientRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // Status filter + offset pager (server-side ?status/?limit/?offset). "open"
  // fetches unfiltered and hides terminal rows client-side (the backend takes
  // one exact status); "all" shows everything.
  const [statusF, setStatusF] = useState<string>("open");
  const [offset, setOffset] = useState(0);

  function clientName(id: string): string {
    return clients.find((c) => c.id === id)?.name ?? id ?? "—";
  }

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (statusF !== "open" && statusF !== "all") qs.set("status", statusF);
      qs.set("limit", String(PAGE_SIZE));
      qs.set("offset", String(offset));
      const res = await api.get<{ recharge_requests: RechargeRequest[] }>(
        `/admin/recharge-requests?${qs.toString()}`,
      );
      setRequests(res.recharge_requests ?? []);
      const c = await api
        .get<{ clients: ClientRef[] }>("/admin/clients")
        .catch(() => null);
      if (c) setClients(c.clients ?? []);
    } catch (err) {
      setError(errMessage(err));
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [statusF, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  // The default "Open" view hides terminal rows CLIENT-SIDE within the page.
  const visibleRequests =
    statusF === "open"
      ? requests.filter((r) => !TERMINAL_STATES.includes(r.status))
      : requests;

  async function advance(req: RechargeRequest) {
    const next = flowOf(req.status).next;
    if (!next) return;
    setBusy(req.id);
    try {
      await api.post(`/admin/recharge-requests/${req.id}/${next.transition}`);
      toast.success(`${clientName(req.client_id)}: ${next.label.toLowerCase()} done`);
      await load();
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function reject(req: RechargeRequest) {
    setBusy(req.id);
    try {
      await api.post(`/admin/recharge-requests/${req.id}/reject`);
      toast.success(`${clientName(req.client_id)}: recharge rejected`);
      await load();
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setBusy(null);
    }
  }

  const columns: Column<RechargeRequest>[] = [
    {
      key: "request",
      header: "Request",
      cell: (r) => (
        <div className="leading-tight">
          <div className="font-mono text-xs text-nx-text-3 tabular-nums">
            {r.id}
          </div>
          <div className="font-medium text-nx-text">
            {clientName(r.client_id)}
          </div>
        </div>
      ),
    },
    {
      key: "account",
      header: "Account",
      cell: (r) => (
        <span className="font-mono text-xs text-nx-text-2 tabular-nums">
          {r.account_id}
        </span>
      ),
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      cell: (r) => <MoneyCell value={r.amount} tone="credit" />,
    },
    {
      key: "budget",
      header: "Budget check",
      cell: (r) =>
        r.budget_ok ? (
          <ToneBadge tone="credit">Within balance</ToneBadge>
        ) : (
          <ToneBadge tone="danger">Exceeds balance</ToneBadge>
        ),
    },
    {
      key: "stepper",
      header: "State",
      cell: (r) =>
        r.status === "rejected" ? (
          <ToneBadge tone="danger">Rejected</ToneBadge>
        ) : (
          <Stepper steps={STEPS} current={flowOf(r.status).index} />
        ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (r) => {
        if (r.status === "rejected" || r.status === "done") {
          return <span className="text-nx-text-3">—</span>;
        }
        const next = flowOf(r.status).next;
        return (
          <div className="flex justify-end gap-2">
            {next ? (
              <Button
                size="xs"
                disabled={busy === r.id}
                onClick={() => advance(r)}
              >
                {next.label}
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="xs"
              disabled={busy === r.id}
              onClick={() => reject(r)}
              className="text-nx-debit hover:text-nx-debit"
            >
              Reject
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <AdminShell>
      <div className="mb-5">
        <h2 className="font-heading text-lg font-semibold">
          Recharge-request queue
        </h2>
        <p className="mt-1 max-w-2xl text-sm text-nx-text-2">
          Cross-tenant recharge inbox. The budget check is resolved server-side
          against the client&apos;s net budget — approve within balance, scrutinize
          when it exceeds.
        </p>
      </div>

      {/* Status filter — "Open" (default) hides terminal done/rejected rows;
          a specific status is applied SERVER-SIDE via ?status. */}
      <div className="mb-4 max-w-xs">
        <NativeSelect
          aria-label="Filter by status"
          value={statusF}
          onChange={(e) => {
            setStatusF(e.target.value);
            setOffset(0);
          }}
        >
          <option value="open">Open (hide done/rejected)</option>
          <option value="all">All statuses</option>
          {ALL_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.replace(/_/g, " ")}
            </option>
          ))}
        </NativeSelect>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>Could not load the queue</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="grid gap-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={visibleRequests}
            rowKey={(r) => r.id}
            empty="No recharge requests pending."
          />
          <Pager
            offset={offset}
            pageSize={PAGE_SIZE}
            count={requests.length}
            loading={loading}
            onOffset={setOffset}
          />
        </>
      )}
    </AdminShell>
  );
}
